import { useCallback } from 'react';
import { useStore } from 'react-redux';
import { useAvatarContext } from '@avatar/context/AvatarProvider';
import { AppState } from 'store';
import { toast } from 'components/Toaster';
import {
  AvatarData,
  TranslationLanguage,
} from 'components/Prompts/TranslatePrompt';
import { useAppDispatch } from 'store/hooks';
import { useStudioProjectContext } from 'pages/dashboard/studio/StudioProjectProvider';
import { useTranslateProjectMutation } from 'components/Projects/operation.generated';
import { ProjectFragment } from 'components/fragments.generated';
import { SectionsQueryHookResult } from 'components/ScriptStudio/Sections/operation.generated';
import { setProjectDrafts } from 'store/ScriptStudioBuffersSlice';
import {
  buildSectionView,
  SectionViewOptions,
} from 'components/ScriptStudio/Sections/selectors';
import useSectionViewOptions from 'components/ScriptStudio/Sections/hooks/useSectionViewOptions';
import { EventName, useEventsContext } from 'context/events/EventsContext';
import { ProjectCardOriginPage } from 'components/Cards/project/ProjectCard';
import useUniversalProjectActors from 'components/avatar/hooks/useUniversalProjectActors';
import { getRegionalAvatars } from 'components/avatar/utils/avatar';
import {
  ProjectSpec,
  Section,
} from '../../../../server/graphql/__generated.types';
import { LocaleEnum_V2 } from '../../../../server/graphql/__generated-graphcms.types';

const toLocaleEnum = (locale: string): LocaleEnum_V2 => {
  const normalizedLocale = locale.replace('-', '_');
  const match = Object.values(LocaleEnum_V2).find(v => v === normalizedLocale);
  if (match !== undefined) return match;
  throw new Error(`Unknown locale: ${normalizedLocale}`);
};

const buildSectionsView = (
  sections: Section[],
  buffers: Record<
    string,
    {
      textDraft?: string;
      formattedTextDraft?: string;
      avatarDraft?: number;
      avatarVariantDraft?: number;
      avatarVariantVersionDraft?: string;
      lastEditedAt: number;
      projectId: string;
    }
  >,
  options: SectionViewOptions
) =>
  sections.map(section => {
    const { text, formattedText, avatar, avatarVariant, avatarVariantVersion } =
      buildSectionView(
        section,
        buffers[section.id],
        undefined,
        undefined,
        options
      );
    return {
      sectionId: section.id,
      name: section.name,
      text,
      formattedText,
      avatarId: avatar,
      avatarVariantId: avatarVariant,
      defaultTTSVersion: avatarVariantVersion,
    };
  });

const setDraftsWithTranslatedSections = (
  dispatch: ReturnType<typeof useAppDispatch>,
  projectSpec: ProjectSpec
) => {
  const projectSpecSections = projectSpec.sections;

  const draftSections = projectSpecSections.map(section => ({
    id: section.id,
    plainText: section.text,
    formattedText: section.formattedText,
    avatarId: section.avatarId,
    avatarVariantId: section.avatarVariantId,
    avatarVariantVersion: section.defaultTTSVersion ?? undefined,
  }));

  if (draftSections.length > 0) {
    dispatch(
      setProjectDrafts({ projectId: projectSpec.id, sections: draftSections })
    );
  }
};

interface UseTranslateProjectProps {
  project: ProjectFragment;
  pageOrigin?: ProjectCardOriginPage;
  refetchSections: SectionsQueryHookResult['refetch'];
}

const useTranslateProject = (props: UseTranslateProjectProps) => {
  const { project, pageOrigin, refetchSections } = props;
  const store = useStore<AppState>();
  const { studioVersion, name: projectName, sampleRate } = project;
  // todo: see about removing this in favor of using the avatar context
  const { projectAvatars: studioProjectAvatars } = useStudioProjectContext();
  const cardProjectAvatars = useUniversalProjectActors({
    skip: pageOrigin === ProjectCardOriginPage.StudioNavbar,
  });
  const projectAvatars =
    pageOrigin === ProjectCardOriginPage.StudioNavbar
      ? studioProjectAvatars
      : cardProjectAvatars;
  const { queryAvatar } = useAvatarContext();
  const sectionViewOptions = useSectionViewOptions();

  const [translateProject] = useTranslateProjectMutation();
  const dispatch = useAppDispatch();
  const [eventTracking] = useEventsContext();

  const handleAvatarSelectionStep = useCallback(
    async (targetLocale: LocaleEnum_V2) => {
      try {
        const { data } = await refetchSections();
        const sections = data.sections.edges.map(e => e.node);

        const sectionsView = buildSectionsView(
          sections,
          store.getState().scriptStudioBuffers.byId,
          sectionViewOptions
        );
        const projectSectionAvatars = sectionsView.flatMap(section => {
          const { avatar, variant } = queryAvatar({
            avatarId: section.avatarId,
            variantId: section.avatarVariantId,
          });
          if (!avatar || !variant) return [];
          return [
            {
              id: avatar.id,
              variantId: variant.id,
              name: avatar.name,
              imageUrl: avatar.image,
              locale: toLocaleEnum(variant.locale),
              version: variant.version,
            },
          ];
        });

        // dedup avatars so there is only one per section avatar
        const projectAvatarsToDisplay = [
          ...new Map(projectSectionAvatars.map(a => [a.id, a])).values(),
        ];

        const regionalAvatars = getRegionalAvatars(
          targetLocale,
          projectAvatars
        );
        const selectionAvatars = regionalAvatars.flatMap(avatar =>
          avatar.variants.map(variant => ({
            id: avatar.id,
            variantId: variant.id,
            name: avatar.name,
            imageUrl: avatar.image,
            locale: toLocaleEnum(variant.locale),
            version: variant.version,
          }))
        );

        // intentionally one entry per avatar id — users pick an avatar, not a specific variant
        const selectionAvatarsToDisplay = [
          ...new Map(selectionAvatars.map(a => [a.id, a])).values(),
        ];

        return { projectAvatarsToDisplay, selectionAvatarsToDisplay };
      } catch (error) {
        toast({
          message: 'Failed to load avatar options',
          type: 'error',
        });
      }
    },
    [refetchSections, projectAvatars, queryAvatar, store, sectionViewOptions]
  );

  const handleTranslate = useCallback(
    async (
      sourceLanguage: TranslationLanguage,
      targetLanguage: TranslationLanguage,
      avatarMappings: Map<number, AvatarData>
    ) => {
      try {
        const { data } = await refetchSections();
        const sections = data.sections.edges.map(e => e.node);

        const sectionsView = buildSectionsView(
          sections,
          store.getState().scriptStudioBuffers.byId,
          sectionViewOptions
        );
        const sectionSpecsInput = sectionsView.map(section => {
          const selectedAvatar = avatarMappings.get(section.avatarId);
          if (!selectedAvatar)
            throw new Error(
              `No avatar selected for section ${section.sectionId}`
            );
          return {
            ...section,
            avatarId: selectedAvatar.id,
            avatarVariantId: selectedAvatar.variantId,
            defaultTTSVersion: selectedAvatar.version,
          };
        });

        const result = await translateProject({
          variables: {
            inputs: {
              projectId: project.id,
              name: projectName,
              driveId: project.folderId ? undefined : project.rootDriveId,
              folderId: project.folderId,
              studioVersion,
              sectionSpecsInput,
              sampleRate,
              translationContext: {
                sourceLanguage: sourceLanguage.languageAndCountry,
                targetLanguage: targetLanguage.languageAndCountry,
              },
            },
          },
        });
        const projectSpec = result.data?.translateProject?.projectSpec;

        if (!projectSpec) throw new Error('Failed to translate project');

        setDraftsWithTranslatedSections(dispatch, projectSpec);

        eventTracking.track({
          type: EventName.ProjectTranslation,
          data: {
            sourceProjectId: project.id,
            translatedProjectId: projectSpec.id,
            targetLanguage: targetLanguage.languageAndCountry,
            sourceLanguage: sourceLanguage.languageAndCountry,
            success: true,
            error: '',
          },
        });

        return `/dashboard/studio/${projectSpec.id}`;
      } catch (error) {
        toast({
          message: 'Failed to translate project',
          type: 'error',
        });

        eventTracking.track({
          type: EventName.ProjectTranslation,
          data: {
            sourceProjectId: project.id,
            translatedProjectId: '',
            targetLanguage: targetLanguage.languageAndCountry,
            sourceLanguage: sourceLanguage.languageAndCountry,
            success: false,
            error: error instanceof Error ? error.message : '',
          },
        });
      }
    },
    [
      dispatch,
      eventTracking,
      project.folderId,
      project.id,
      project.rootDriveId,
      projectName,
      refetchSections,
      sampleRate,
      sectionViewOptions,
      store,
      studioVersion,
      translateProject,
    ]
  );

  return {
    onTranslate: handleTranslate,
    onAvatarSelectionStep: handleAvatarSelectionStep,
  };
};

export default useTranslateProject;
